import { customElement, GenesisElement, observable } from '@genesislcap/web-core';
import { syndicateInfoTemplate as template } from './syndicate-info.template';
import { syndicateInfoStyles as styles } from './syndicate-info.styles';

export interface SyndicateRole {
  role: string;
  members: string[];
}

@customElement({
  name: 'syndicate-info',
  template,
  styles,
})
export class SyndicateInfo extends GenesisElement {
  @observable syndicateRoles: SyndicateRole[] = [];
  @observable leadShortName: string = '';
  @observable showTooltip: boolean = false;
  @observable deal: any = null;
  
  @observable syndicateTooltip: HTMLElement;

  // Automatically compute syndicate data when deal changes
  dealChanged(): void {
    if (this.deal?.SYNDICATE) {
      this.computeSyndicateData(this.deal.SYNDICATE);
    } else {
      // Reset to empty state when no syndicate data
      this.syndicateRoles = [];
      this.leadShortName = '';
    }
  }

  private computeSyndicateData(syndicateData: string): void {
    const raw: string = syndicateData || '';

    const allParties = raw
      .split(/[;]/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    // If no valid parties found, set empty array
    if (allParties.length === 0) {
      this.syndicateRoles = [];
      this.leadShortName = '';
      return;
    }

    const lead = allParties[0] || '';
    const otherParties = allParties.slice(1);

    if(otherParties.length === 0) {
      this.syndicateRoles = [
        { role: 'Syndicate Lead', members: [lead] }
      ];
    }
    else {
        this.syndicateRoles = [
        { role: 'Syndicate Lead', members: [lead] },
        { role: 'Members', members: otherParties }
      ];
    }
    this.leadShortName = lead;
  }

  toggleTooltip(): void {
    this.showTooltip = !this.showTooltip;
  }

  closeSyndicateTooltip(): void {
    this.showTooltip = false;
  }

  handleDocumentClick(e: Event): void {
    if (!this.showTooltip) {
      return;
    }
    const path = e.composedPath();
    if (this.syndicateTooltip && !path.includes(this.syndicateTooltip)) {
      this.closeSyndicateTooltip();
    }
  }

  private handleDocumentClickBound: ((e) => void) | null = null;
  private clickTimeout: number | null = null;

  showTooltipChanged(oldValue: boolean, newValue: boolean): void {
    // Clear any pending timeout
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }

    if (newValue) {
      // setTimeout(0) defers listener attachment to next event loop tick.
      // Without this, the opening click event would bubble to document and immediately close the tooltip.
      this.clickTimeout = window.setTimeout(() => {
        if (!this.handleDocumentClickBound) {
          this.handleDocumentClickBound = this.handleDocumentClick.bind(this);
        }
        document.addEventListener('click', this.handleDocumentClickBound);
      }, 0);
    } else {
      // When closing, remove listener immediately
      if (this.handleDocumentClickBound) {
        document.removeEventListener('click', this.handleDocumentClickBound);
      }
    }
  }

  disconnectedCallback(): void {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
    }
    if (this.handleDocumentClickBound) {
      document.removeEventListener('click', this.handleDocumentClickBound);
    }
    super.disconnectedCallback();
  }

  connectedCallback(): void {
    super.connectedCallback();
  }
} 
